import { Result } from "@/api/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ImpactMethod } from "./useImpactMethod";
import useSmartQueryClient from "./useSmartQueryClient";

const queryKey = ["companySettings"];

export interface CompanySettings {
  show_completed: boolean;
  is_calculating_impact: boolean;
  calculation_started_at: string;
  company_name: string;
  calculation_requested_by: string;
  transport_show_by_category: boolean;
  show_dashboard: boolean;
  company_type: "production" | "test";
  include_eol: boolean;
  in_maintenance: boolean;
  include_usage: boolean;
  impact_method: ImpactMethod;
  access: string[];
  reporting_start_year: number;
  reporting_end_year: number;
  fiscal_month: number;
  show_onboarding: boolean;
}

export function useCompanySettings() {
  return useQuery({
    queryFn: () => axios.get<CompanySettings>(`/company_settings`).then((data) => data.data),
    queryKey,
  });
}

export function useCompanyAccess() {
  const companySettings = useCompanySettings();
  return companySettings?.data?.access;
}

export function usePatchCompanySettings() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: (settings: Partial<CompanySettings>) =>
      axios.patch<Result>(`/company_settings`, settings),
    onMutate: (data) => {
      smartQueryClient.setQueryData<CompanySettings>(queryKey, (companySettings) => {
        if (!companySettings) return undefined;
        const newCompanySettings = {
          ...companySettings,
          ...data,
        };
        return newCompanySettings;
      });
    },
    onSuccess: () => {
      smartQueryClient.invalidateBaseQueries();
    },
    mutationKey: ["patchCompanySettings"],
  });
}
